import * as React from "react";

import {translate} from "react-i18next";
import {t} from "i18next";
import {Icon} from "semantic-ui-react";

import Dialog from "../../../components/Dialogs/Dialog";
import Button from "../../../components/Button";
import {DtoListWithLinksOfOptionDto} from "../../../services/Client";

import DataAdapterOptionsView from "../DataAdapterOptionsView";

import {EStepTab, StepHeader} from "./MODUtils";
import {MOD_OVERVIEW_PAGE_SIZE, MODOverviewTable} from "./MODOverviewTable";
import {MODConfirmTab} from "./MODConfirmTab";

export interface IMODOverviewPage {
    items: DataAdapterOptionsView[];
    total: number;
}

interface IWorkflowStatusMultiUpdateDialogProps {
    items: DataAdapterOptionsView[];
    multipleOptionDeletionResult?: number[];
    requestedCount?: number;
    selectAllMode?: boolean;
    selectAllTotal?: number;
    excludedIds?: number[];
    fetchOverviewPage?: (page: number, size: number) => Promise<IMODOverviewPage>;
    onSubmitSelectAll?: (excludedIds: number[]) => void;
    onSubmit: (options: DtoListWithLinksOfOptionDto) => void;
    onClose: () => void;
    onRefresh: () => void;
}

interface IWorkflowStatusMultiUpdateDialogState {
    activeTab: number;
    loaderActive: boolean;
    tablePage: number;
    selectedItemsOnPreviewScreen:number[];
    overviewItems: DataAdapterOptionsView[];
    overviewTotal: number;
    overviewLoading: boolean;
    overviewError: boolean;
    excludedOnPreview: number[];
}

class WorkflowStatusMultiUpdateDialog extends React.Component<
  IWorkflowStatusMultiUpdateDialogProps,
  IWorkflowStatusMultiUpdateDialogState
> {
  private unmounted = false;

  constructor(props: IWorkflowStatusMultiUpdateDialogProps) {
    super(props);

    this.state = {
      activeTab: EStepTab.OverviewTab,
      loaderActive: false,
      tablePage: 1,
      selectedItemsOnPreviewScreen: [],
      overviewItems: [],
      overviewTotal: props.selectAllTotal || 0,
      overviewLoading: false,
      overviewError: false,
      excludedOnPreview: props.excludedIds ? props.excludedIds.slice() : []
    };
  }

  componentDidMount(): void {
    this.setState({
        selectedItemsOnPreviewScreen:this.props.items.map(
            (item) => item.objectId
          ),
     });
    if (this.props.selectAllMode) {
      this.loadOverviewPage(1);
    }
  }
  componentWillUnmount(): void {
    this.unmounted = true;
  }
  componentDidUpdate(
    prevProps: Readonly<IWorkflowStatusMultiUpdateDialogProps>
  ): void {
    if (
      this.props.multipleOptionDeletionResult !== undefined &&
      prevProps.multipleOptionDeletionResult !==
        this.props.multipleOptionDeletionResult
    ) {
      this.setState({
        activeTab: EStepTab.ResultTab,
        loaderActive: false,
      });
    }
  }

  private loadOverviewPage = (page: number) => {
    if (!this.props.fetchOverviewPage) {
      return;
    }
    this.setState({ overviewLoading: true, overviewError: false, tablePage: page });
    this.props.fetchOverviewPage(page - 1, MOD_OVERVIEW_PAGE_SIZE)
      .then((result: IMODOverviewPage) => {
        if (this.unmounted) { return; }
        this.setState({
          overviewItems: result.items || [],
          overviewTotal: result.total !== undefined ? result.total : this.state.overviewTotal,
          overviewLoading: false
        });
      })
      .catch(() => {
        if (this.unmounted) { return; }
        this.setState({ overviewItems: [], overviewLoading: false, overviewError: true });
      });
  }

  private onSubmit = () => {
    this.setState({ loaderActive: true, tablePage: 1 });
    if (this.props.selectAllMode) {
      if (this.props.onSubmitSelectAll) {
        this.props.onSubmitSelectAll(this.state.excludedOnPreview);
      } else {
        this.props.onSubmit({ list: [] });
      }
      return;
    }
    const finalList = this.props.items.filter(
      (item) =>
        this.state.selectedItemsOnPreviewScreen.indexOf(item.objectId) !== -1
    );
    this.props.onSubmit({ list: finalList.map(item => item.dto)});
  }

  private getNumberOfOptions = (): number =>
    this.props.selectAllMode
      ? Math.max(0, (this.props.selectAllTotal !== undefined ? this.props.selectAllTotal : this.state.overviewTotal)
          - this.state.excludedOnPreview.length)
      : this.state.selectedItemsOnPreviewScreen.length

  private renderSelectAllOverview = () => {
    const excluded = this.state.excludedOnPreview;
    const checkedOnPage = this.state.overviewItems
      .filter((item) => excluded.indexOf(item.objectId) === -1)
      .map((item) => item.objectId);
    return (
      <React.Fragment>
        <div style={{ marginBottom: "10px" }}>
          <Icon name="info circle" color="blue" />
          {t("PSL_MULTI_DLT:SELECT_ALL_OVERVIEW", {
            count: this.getNumberOfOptions(),
            defaultValue: "All {{count}} options matching the current filter are selected. Uncheck a row to keep it."
          })}
        </div>
        {this.state.overviewError && (
          <div style={{ marginBottom: "10px", color: "red" }}>
            <Icon name="exclamation triangle" color="red" />
            {t("PSL_MULTI_DLT:OVERVIEW_LOAD_ERROR", {
              defaultValue: "The overview page could not be loaded. You can still continue – the deletion is resolved on the server by the current filter."
            })}
          </div>
        )}
        <MODOverviewTable
          items={this.state.overviewItems}
          serverPaged={true}
          totalItems={this.state.overviewTotal}
          loading={this.state.overviewLoading}
          tablePage={this.state.tablePage}
          t={t}
          nextAction={() => this.setState({ activeTab: EStepTab.ConfirmTab })}
          nextDisabled={this.getNumberOfOptions() === 0 || this.state.overviewLoading}
          onChangePage={(page: number) => this.loadOverviewPage(page)}
          isResultTab={false}
          onRemove={this.onRemove}
          selectedItemsOnPreviewScreen={checkedOnPage}
        />
      </React.Fragment>
    );
  }
  onRemove = (id: number, isSelected: boolean) => {
    if (this.props.selectAllMode) {
      const excluded = this.state.excludedOnPreview;
      if (isSelected) {
        this.setState({ excludedOnPreview: excluded.filter((p) => p !== id) });
      } else if (excluded.indexOf(id) === -1) {
        this.setState({ excludedOnPreview: [...excluded, id] });
      }
      return;
    }
    const currentSelectedItemsOnPreviewScreen =
    this.state.selectedItemsOnPreviewScreen;
    if (isSelected) {
        if(currentSelectedItemsOnPreviewScreen.indexOf(id) === -1) {
            this.setState({
                selectedItemsOnPreviewScreen: [
                  ...currentSelectedItemsOnPreviewScreen,
                  id
                ],
              });
        }
    } else {
      this.setState({
        selectedItemsOnPreviewScreen:
          currentSelectedItemsOnPreviewScreen.filter((p) => p !== id),
      });
    }
  }

  render() {
    const dlgButton =
      this.state.activeTab === EStepTab.ResultTab ? (
        <Button
          key={"refreshButton"}
          icon={"refresh"}
          onClick={this.props.onRefresh}
          disabled={this.state.loaderActive}
        />
      ) : (
        <Button
          key={"cancelButton"}
          icon={"ban"}
          onClick={this.props.onClose}
          disabled={this.state.loaderActive}
        />
      );

    return (
      <Dialog
        open={true}
        title={t("PSL_MULTI_DLT:DIALOG_TITLE")}
        customStyleClassName={"multi-step-dialog"}
        actions={[dlgButton]}
      >
        <div style={{ marginBottom: "30px" }}>
          <StepHeader activeTab={this.state.activeTab} t={t} />
        </div>

        {/** First Tab */}
        {this.state.activeTab === EStepTab.OverviewTab && this.props.selectAllMode &&
          this.renderSelectAllOverview()}
        {this.state.activeTab === EStepTab.OverviewTab && !this.props.selectAllMode && (
          <MODOverviewTable
            items={this.props.items}
            tablePage={this.state.tablePage}
            t={t}
            nextAction={() => this.setState({ activeTab: EStepTab.ConfirmTab })}
            onChangePage={(page: number) => this.setState({ tablePage: page })}
            isResultTab={false}
            onRemove={this.onRemove}
            selectedItemsOnPreviewScreen={
              this.state.selectedItemsOnPreviewScreen
            }
          />
        )}

        {/** Second Tab */}
        {this.state.activeTab === EStepTab.ConfirmTab &&
          this.getNumberOfOptions() > 0 && (
            <MODConfirmTab
              loaderActive={this.state.loaderActive}
              numberOfOptions={this.getNumberOfOptions()}
              t={t}
              onSubmit={this.onSubmit}
              backAction={() =>
                this.setState({ activeTab: this.state.activeTab - 1 })
              }
            />
          )}

        {/** Third Tab */}
        {this.state.activeTab === EStepTab.ResultTab &&
          this.props.multipleOptionDeletionResult !== undefined && (
            <>
              <p>
                <b>
                  {(this.props.requestedCount !== undefined
                    ? this.props.requestedCount
                    : this.getNumberOfOptions()) -
                    this.props.multipleOptionDeletionResult.length}
                </b>
                {" " + t("PSL_MULTI_DLT:RESULT_SUCCESS")}
              </p>
              {this.props.multipleOptionDeletionResult.length > 0 && (
                <React.Fragment>
                  <div className={"display--flex"} style={{ marginBottom: 20 }}>
                    <Icon name="exclamation triangle" color="red" />
                    <p>
                      <b style={{ color: "red" }}>
                        {this.props.multipleOptionDeletionResult.length}
                      </b>
                      {" " + t("PSL_MULTI_DLT:RESULT_ERROR")}
                      {" " + t("PSL_MULTI_DLT:RESULT_ERROR_DISCLAIMER")}
                    </p>
                  </div>

                  {this.props.selectAllMode ? (
                    <p style={{ wordBreak: "break-all" }}>
                      {t("PSL_MULTI_DLT:RESULT_FAILED_IDS", {
                        defaultValue: "The following option IDs could not be deleted:"
                      }) + " "}
                      <b>{this.props.multipleOptionDeletionResult.join(", ")}</b>
                    </p>
                  ) : (
                    <MODOverviewTable
                      items={this.props.items.filter((item) =>
                        // @ts-ignore already checked
                        this.props.multipleOptionDeletionResult.includes(
                          item.objectId
                        )
                      )}
                      tablePage={this.state.tablePage}
                      t={t}
                      onChangePage={(page: number) =>
                        this.setState({ tablePage: page })
                      }
                      isResultTab={true}
                      selectedItemsOnPreviewScreen={
                        this.state.selectedItemsOnPreviewScreen
                      }
                      onRemove={this.onRemove}
                    />
                  )}
                </React.Fragment>
              )}
            </>
          )}
      </Dialog>
    );
  }
}

export default translate("PSL_MULTI_DLT")(WorkflowStatusMultiUpdateDialog);



// WEBPACK FOOTER //
// ./src/views/OptionsView/multipleOptionDeletionDialog/MultipleOptionDeletionDialog.tsx